import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, Star } from "lucide-react";
import type { Benefit } from "../types/benefit";
import Badge from "./Badge";
import { pushGA } from "../lib/utils";

interface BenefitCardProps {
  benefit: Benefit;
  viewMode?: 'grid' | 'list';
}

export default function BenefitCard({ benefit, viewMode = 'grid' }: BenefitCardProps) {
  const isHighlight = benefit.destaque?.toLowerCase() === "sim";

  const handleClick = () => {
    pushGA('benefit_click', { 
      benefit_name: benefit.nome, 
      category: benefit.categoria,
      is_highlight: isHighlight
    });
  };

  if (viewMode === 'list') {
    return (
      <motion.article
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="group relative overflow-hidden rounded-2xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black shadow-lg hover:shadow-xl hover:shadow-darling-lilac/10 dark:hover:shadow-darling-lilac/20 transition-all duration-300"
      >
        <div className="flex flex-col md:flex-row">
          {/* Image */}
          <div className="relative md:w-64 flex-shrink-0">
            {/* Highlight Badge */}
            {isHighlight && (
              <div className="absolute top-4 right-4 z-10">
                <Badge className="bg-sun-flower/20 text-sun-flower border-sun-flower/30 dark:bg-sun-flower/10 dark:text-sun-flower dark:border-sun-flower/20">
                  <Star className="h-3 w-3 fill-current" />
                  Destaque
                </Badge>
              </div>
            )}
            
            {benefit.imagem ? (
              <div className="aspect-[16/9] md:aspect-square w-full bg-light-gray dark:bg-deep-purple/30 overflow-hidden">
                <img 
                  src={benefit.imagem} 
                  alt={benefit.nome}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="aspect-[16/9] md:aspect-square w-full grid place-items-center bg-gradient-to-br from-light-gray to-light-gray-alt dark:from-deep-purple/20 dark:to-soft-black/50">
                <ExternalLink className="h-12 w-12 text-darling-lilac/60"/>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 p-6 flex flex-col h-full">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <Badge className="bg-darling-lilac/10 text-darling-lilac border-darling-lilac/20 dark:bg-darling-lilac/20 dark:text-darling-lilac dark:border-darling-lilac/30 mb-3">
                  {benefit.categoria}
                </Badge>
                <h3 className="text-xl font-exo font-bold leading-tight text-soft-black dark:text-light-gray/95 group-hover:text-darling-lilac dark:group-hover:text-darling-lilac transition-colors mb-3">
                  {benefit.nome}
                </h3>
              </div>
            </div>

            <p className="text-sm text-soft-black/80 dark:text-light-gray/90 leading-relaxed font-exo font-light flex-1">
              {benefit.descricao}
            </p>

            <div className="mt-auto">
              <a
                href={benefit.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClick}
                className="inline-flex items-center justify-center gap-2 w-full md:w-auto rounded-xl border border-darling-lilac/30 bg-darling-lilac text-white hover:bg-deep-purple dark:border-darling-lilac dark:bg-darling-lilac dark:hover:bg-deep-purple px-4 py-2.5 text-sm font-exo font-semibold transition-colors shadow-sm"
              >
                <ExternalLink className="h-4 w-4" />
                Acessar benefício
              </a>
            </div>
          </div>
        </div>
      </motion.article>
    );
  }

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="group relative overflow-hidden rounded-2xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black shadow-lg hover:shadow-xl hover:shadow-darling-lilac/10 dark:hover:shadow-darling-lilac/20 transition-all duration-300 flex flex-col h-full"
    >
      {/* Highlight Badge */}
      {isHighlight && (
        <div className="absolute top-4 right-4 z-10">
          <Badge className="bg-sun-flower/20 text-sun-flower border-sun-flower/30 dark:bg-sun-flower/10 dark:text-sun-flower dark:border-sun-flower/20">
            <Star className="h-3 w-3 fill-current" />
            Destaque
          </Badge>
        </div>
      )}

      {/* Image */}
      {benefit.imagem ? (
        <div className="aspect-[16/9] w-full bg-light-gray dark:bg-deep-purple/30 overflow-hidden">
          <img 
            src={benefit.imagem} 
            alt={benefit.nome}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="aspect-[16/9] w-full grid place-items-center bg-gradient-to-br from-light-gray to-light-gray-alt dark:from-deep-purple/20 dark:to-soft-black/50">
          <ExternalLink className="h-12 w-12 text-darling-lilac/60"/>
        </div>
      )}

      {/* Content */}
      <div className="p-6 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <Badge className="bg-darling-lilac/10 text-darling-lilac border-darling-lilac/20 dark:bg-darling-lilac/20 dark:text-darling-lilac dark:border-darling-lilac/30 mb-3">
              {benefit.categoria}
            </Badge>
            <h3 className="text-xl font-exo font-bold leading-tight text-soft-black dark:text-light-gray/95 group-hover:text-darling-lilac dark:group-hover:text-darling-lilac transition-colors">
              {benefit.nome}
            </h3>
          </div>
        </div>

        <p className="text-sm text-soft-black/80 dark:text-light-gray/90 line-clamp-3 leading-relaxed font-exo font-light flex-1">
          {benefit.descricao}
        </p>

        <div className="mt-auto pt-4">
          <a
            href={benefit.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="inline-flex items-center justify-center gap-2 w-full rounded-xl border border-darling-lilac/30 bg-darling-lilac text-white hover:bg-deep-purple dark:border-darling-lilac dark:bg-darling-lilac dark:hover:bg-deep-purple px-4 py-2.5 text-sm font-exo font-semibold transition-colors shadow-sm"
          >
            <ExternalLink className="h-4 w-4" />
            Acessar benefício
          </a>
        </div>
      </div>
    </motion.article>
  );
}